import React, { useState, useEffect } from "react";
import { Toast } from "react-bootstrap";
import ReactDOM from "react-dom";

export type ToastType = "success" | "error" | "warning" | "info";

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  show: boolean;
}

let toastItems: ToastItem[] = [];
let maxToasts = 1;
let updateCallback: (() => void) | null = null;

const updateToasts = () => {
  if (updateCallback) {
    updateCallback();
  }
};

const addToast = (message: string, type: ToastType) => {
  const newToast: ToastItem = {
    id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    message,
    type,
    show: true,
  };
  toastItems.push(newToast);
  if (toastItems.length > maxToasts) {
    toastItems = toastItems.slice(-maxToasts);
  }

  updateToasts();
  setTimeout(() => {
    removeToast(newToast.id);
  }, 2000);
};

const removeToast = (id: string) => {
  toastItems = toastItems.filter((toast) => toast.id !== id);
  updateToasts();
};

const setMaxToasts = (max: number) => {
  maxToasts = max;
  if (toastItems.length > maxToasts) {
    toastItems = toastItems.slice(-maxToasts);
    updateToasts();
  }
};

const clearAllToasts = () => {
  toastItems = [];
  updateToasts();
};

export const toast = {
  success: (message: string) => addToast(message, "success"),
  fail: (message?: unknown) => {
    const normalizedMessage =
      typeof message === "string"
        ? message
        : message != null
          ? String(message)
          : "";

    if (normalizedMessage.includes("unique constraint failed")) {
      addToast(
        "File with this name already exists. Please rename and retry",
        "error"
      );
    } else {
      addToast(
        normalizedMessage ||
          "Could not process your request. Please retry in a moment.",
        "error"
      );
    }
  },
  warning: (message: string) => addToast(message, "warning"),
  info: (message: string) => addToast(message, "info"),
  setMaxToasts,
  clearAll: clearAllToasts,
};

const ToastComponent: React.FC = () => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    updateCallback = () => forceUpdate({});
    return () => {
      updateCallback = null;
    };
  }, []);

  const getToastClasses = (type: ToastType) => {
    switch (type) {
      case "success":
        return "bg-success";
      case "error":
        return "bg-danger";
      case "warning":
        return "bg-warning";
      case "info":
        return "bg-info";
      default:
        return "bg-primary";
    }
  };

  const getIconClasses = (type: ToastType) => {
    switch (type) {
      case "success":
        return "bi-check-circle-fill text-white f-14";
      case "error":
        return "bi-ban text-white";
      case "warning":
        return "bi bi-exclamation-triangle-fill me-2";
      case "info":
        return "bi bi-info-circle-fill me-2";
      default:
        return "bi bi-bell-fill me-2";
    }
  };

  return ReactDOM.createPortal(
    <div
      className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
      style={{ zIndex: 9999, minWidth: "300px", maxWidth: "80%" }}
    >
      {toastItems.length > 0 &&
        toastItems.map((toast) => (
          <div key={toast.id} className="w-100 mb-2">
            <Toast
              show={toast.show}
              onClose={() => removeToast(toast.id)}
              className={`${getToastClasses(toast.type)} w-100`}
              delay={2000}
              autohide
            >
              <Toast.Body className="text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i className={getIconClasses(toast.type)}></i>
                    <span className="ms-2">{toast.message}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Close"
                    onClick={() => removeToast(toast.id)}
                    style={{ cursor: "pointer" }}
                  ></button>
                </div>
              </Toast.Body>
            </Toast>
          </div>
        ))}
    </div>,
    document.body
  );
};

export default ToastComponent;
